import { RFResponse, RFResponseCommon } from "@/interfaces/common";
import {
	Country,
	Currency,
	OrderParams,
	OrderResponse,
	QuickCardModel,
	RateModel,
	ResponseChannel,
	ResponseMasterAccountInfo,
	ResponseNextChannel,
	ResponsePayment,
	ResponsePaymentErrorInfos,
	USDDepositOption,
	WireTransferOrderParams,
	WriteTransferSupportResponse,
} from "@/interfaces/rapydTypes";
import log2Sentry from "@/utils/log2Sentry";
import * as http from "@/utils/request";
import rfStore from "@/utils/rfStore";
import { DepositError } from "@/utils/sentryError";
import useSWR from "swr";

const baseUrl = process.env.NEXT_PUBLIC_BASE_API;

// 获取支付方式
export const getPaymentMethods = async (currency: string, companyCode = "rapyd") => {
	const url = `${baseUrl}/payment/channels/${companyCode}?currency=${currency}`;
	try {
		const { data, code, message } = await http.get<RFResponseCommon<{ category: string }[]>>(url);

		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (err) {
		throw err;
	}
};

// 下单接口
export const createOrder = async (params: OrderParams) => {
	const url = `${baseUrl}/order/api/order`;
	try {
		const { code, data, message } = await http.post<OrderParams, RFResponseCommon<OrderResponse>>(url, params);

		if (code !== 0) {
			throw Error(message);
		}
		console.log(data);
		return data;
	} catch (error) {
		throw error;
	}
};

// currency 是否支持电汇
export const wireTransferIsSupport = async (currency: string) => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/deposit/currency/meta/${currency}`;
	try {
		const { code, data, message } = await http.get<RFResponseCommon<WriteTransferSupportResponse>>(url);

		if (code !== 200) {
			throw Error(message);
		}

		return data.supportWireTransfer;
	} catch (error) {
		console.log(error);
		return false;
	}
};

// 获取入金国家列表
export const getCountriesFromServer = async (language: string) => {
	const url = `${baseUrl}/order/api/payment/countries?language=${language}`;
	try {
		const { data, code, message } = await http.get<RFResponseCommon<Country[]>>(url);
		if (code !== 0) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		console.log(error);
		return [];
	}
};

// 获取下单方式
export const getPayments = async (currency: string) => {
	const url = `${baseUrl}/order/api/payment/channels?currency=${currency}`;

	try {
		const { code, data, message } = await http.get<RFResponseCommon<ResponsePayment>>(url);

		if (code !== 0) {
			throw Error(message);
		}

		return data;
	} catch (error) {
		console.log(error);
		return { region: "", channels: [] };
	}
};

// 获取快捷支付卡list
export const getCreditCards = async (companyCode = "checkout") => {
	const url = `${baseUrl}/order/api/cards/${companyCode}`;
	try {
		const { data, code, message } = await http.get<RFResponseCommon<QuickCardModel[]>>(url);

		if (code !== 0) {
			throw Error(message);
		}

		return data;
	} catch (err) {
		throw err;
	}
};

// 删除 credit Card
export const deleteCreditCard = async (cardId: string) => {
	const url = `${baseUrl}/order/api/order/card/${cardId}`;
	try {
		const { code } = await http._delete<RFResponseCommon<null>>(url);
		return code === 200;
	} catch (error) {
		console.log(error);
		return false;
	}
};

// 获取下的名
export const getUserName = async () => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/name`;

	try {
		const { data, code, message } = await http.get<RFResponseCommon<{ masterAccountNameLocal: string }>>(url);

		if (code !== 200) {
			throw Error(message);
		}

		return data.masterAccountNameLocal || "";
	} catch (err) {
		throw err;
	}
};

// 获取用户默认信息
export const getUserBaseInfo = async () => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/info`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<ResponseMasterAccountInfo>>(url);
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		console.log(error);
	}
};

export const useUserBaseInfo = () => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/info`;
	const { data, isLoading, error } = useSWR(
		url,
		async () => await http.get<RFResponse<ResponseMasterAccountInfo>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		error,
	};
};

// 获取支付错误信息
export const getPaymentErrorInfos = async (orderId: string) => {
	const url = `${baseUrl}/order/api/order/${orderId}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<ResponsePaymentErrorInfos>>(url);
		if (code !== 0) {
			throw Error(message);
		}

		return data;
	} catch (error) {
		throw error;
	}
};

// 获取二级渠道
export const getNextChannels = async (currency: string, categoryKey: string) => {
	const url = `${baseUrl}/order/api/payment/channels/secondary?currency=${currency}&categoryKey=${categoryKey}`;

	try {
		const { code, message, data } = await http.get<RFResponseCommon<ResponseNextChannel[]>>(url);

		console.log(data);
		if (code !== 0) {
			throw Error(message);
		}

		return data;

		// const list = await mockData();
		// return list;
	} catch (error) {}
};

/**
 * 根据货币跟国家获取付款方式
 * currency query
 * region header  设置rfStore
 */
export const getPaymentChannels = async (currency: string) => {
	const url = `${baseUrl}/cashier/api/${currency}/channels`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<ResponseChannel[]>>(url);
		if (code !== 200) throw Error(message);

		return data;
	} catch (error) {
		console.log(error);
	}
};

/**
 * channelCode 查询二级渠道
 */
export const getNextChannelsFromChannel = async (channelCode: string, currency: string) => {
	const url = `${baseUrl}/cashier/api/${channelCode}/secondary/channels?currency=${currency}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<ResponseChannel[]>>(url);

		console.log(data);
		if (code !== 200) {
			throw Error(message);
		}

		return data;
	} catch (error) {
		console.log(error);
	}
};

/**
 * 入金支持的币种
 */
export const getCurrencies = async () => {
	const url = `${baseUrl}/cashier/api/channels/currencies`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<Currency[]>>(url);

		if (code !== 200) {
			throw Error(message);
		}

		return data;
	} catch (error) {
		console.log(error);
		return [];
	}
};

/**
 * 提交电汇入金单子
 */
export const createWireTransferOrder = async (params: WireTransferOrderParams) => {
	const url = `${baseUrl}/masterAccount/api/masterAccounts/deposit`;
	try {
		const { code } = await http.post<WireTransferOrderParams, RFResponseCommon<{ depositId: string }>>(url, params);
		return code === 200;
	} catch (error) {
		throw error;
	}
};

/**
 * 美元-其他币种汇率
 */
export const getExchangeRate = async (params: string[]) => {
	const url = `${baseUrl}/portfolio/api/currencies/batch`;

	try {
		const { code, data = [], message } = await http.post<Array<string>, RFResponseCommon<RateModel[]>>(url, params);

		if (code !== 200) throw Error(message);

		return data;
	} catch (error: any) {
		console.log(error);
		log2Sentry(new DepositError(error.message), "error", error);
	}
};

/**
 * 检查名称是否为同名
 * 在 DCS卡支持的时候使用
 */
export const checkUserName = async (userName: string) => {
	const uid = rfStore.getRFState()?.userId;

	const url = `${baseUrl}/masterAccount/api/masterAccounts/deposit/users/${uid}/match`;
	try {
		const { code, message, data } = await http.patch<{ holderName: string }, RFResponseCommon<boolean>>(url, {
			holderName: userName,
		});
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		console.log(error);
		return false;
	}
};
